import React, { useCallback, useState } from 'react';
import { gql } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import {
  Button,
  HStack,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import PropTypes from 'prop-types';

const FETCH_EVENTS_USERS = gql`
query fetchUserInfo($eventId: Int!, $code: String, $email: String) {
  fetchUserDetail(code: $code, email: $email) {
    firstname
    lastname
    email
    phone
    code
    profileImageUrl
    dateOfBirth
    gender
    id
  }
  fetchEventsUsersByUser(eventId: $eventId, code: $code, email: $email) {
    id
    status
    schoolId
    fetchTeam {
      id
      code
      school {
        id
        name
      }
    }
    fetchMembers {
      id
      fetchEventCompetition {
        id
        title
        status
      }
    }
  }
}
`;

const ADD_USER_TO_COALITION = gql`
mutation addUserToCoalition($id: ID!, $userId: Int!) {
  addUserToCoalition(input: {
    id: $id,
    userId: $userId,
  }) {
    coalition {
      id
      status
      tenantId
      code
      fetchEventCompetition {
        id
        title
      }
      fetchCoalitionsSchools{
        id
        status
        schoolId
        tenantId
        coalitionId
        fetchSchool {
          id
          name
        }
        fetchCoalitionMembers {
          id
          user {
            id
            code
            firstname
            lastname
          }
        }
      }
    }
  }
}
`;

export const AddStudentModal = ({
  isOpen,
  onClose,
}) => {
  const { id, coalitionId } = useParams();
  const eventId = parseInt(id, 10);
  const toast = useToast();
  const [input, setInput] = useState();

  const [fetchEventsUsers, { data, loading }] = useLazyQuery(FETCH_EVENTS_USERS, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      if (res.fetchEventsUsersByUser.length === 0
        && res.fetchCanceledEventsUsersByUser.length === 0) {
        toast({
          title: `Could not find user ${input} in event.`,
          status: 'error',
          isClosable: true,
          position: 'top',
        });
      }
    },
    onError: (err) => {
      toast({
        title: err.graphQLErrors[0]?.message ?? 'An unknown error occurred. Please try again later.',
        status: 'error',
        isClosable: true,
        position: 'top',
      });
    },
  });

  const lookupUser = useCallback(async (evt, search) => {
    evt.preventDefault();
    const variables = { eventId };
    if (search.includes('@')) {
      // it's an email
      variables.email = search;
    } else {
      variables.code = search;
    }

    fetchEventsUsers({ variables });
  }, [fetchEventsUsers, eventId]);

  const [addUserToCoalition, { loading: addUserLoading }] = useMutation(
    ADD_USER_TO_COALITION, {
      onCompleted: () => {
        const user = data.fetchUserDetail;
        toast({
          title: `Successfully added ${user.firstname} ${user.lastname} to coalition`,
          status: 'success',
          isClosable: true,
          position: 'top',
        });
      },
      onError: (err) => {
        toast({
          title: err.graphQLErrors[0]?.message ?? 'An unknown error occurred. Please try again later.',
          status: 'error',
          isClosable: true,
          position: 'top',
        });
      },
    },
  );
  const onConfirm = () => {
    addUserToCoalition({
      variables: {
        id: coalitionId,
        userId: data.fetchUserDetail.id,
      },
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Student</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack>
            <form onSubmit={(e) => lookupUser(e, input)} width="100%">
              <HStack width="100%">
                <Input placeholder="Enter MIST ID or email address" value={input} onChange={(e) => setInput(e.target.value)} />
                <IconButton icon={<FaSearch />} aria-label="search" type="submit" isLoading={loading} />
              </HStack>
            </form>
            {data && (
              <UserInfo user={data.fetchUserDetail} eventsUsers={data.fetchEventsUsersByUser} />
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onConfirm} isLoading={addUserLoading} disabled={loading || !data}>
            Add Student
          </Button>
          <Button colorScheme="gray" variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
AddStudentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const UserInfo = ({ user, eventsUsers }) => {
  const fullname = `${user.firstname} ${user.lastname}`;

  return (
    <HStack alignItems="start" width="100%" marginBlockStart={4}>
      <Image height={12} width={12} borderRadius="100%" src={user.profileImageUrl} />
      <VStack alignItems="start" width="100%">
        <Text>{fullname}</Text>
        <Text>{user.code}</Text>
        <Text>{user.email}</Text>
        {eventsUsers
          .filter((eu) => eu.fetchMembers.length > 0)
          .map((eu) => (
            <div key={eu.id}>
              <Text>{eu.fetchTeam.school.name}</Text>
              <Text>Competitions:</Text>
              {eu.fetchMembers.map((member) => (
                <Text key={member.id}>{member.fetchEventCompetition.title}</Text>
              ))}
            </div>
          ))}
      </VStack>
    </HStack>
  );
};
UserInfo.propTypes = {
  user: PropTypes.objectOf({
    firstname: PropTypes.string.isRequired,
    lastname: PropTypes.string.isRequired,
    profileImageUrl: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  eventsUsers: PropTypes.arrayOf({
    fetchMembers: PropTypes.arrayOf({
      id: PropTypes.string.isRequired,
      fetchEventCompetition: PropTypes.objectOf({
        title: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};
